<template>
  <div class="view-player">
    <dmb-show v-if="show" :orientation="orientation" :show="show"/>

    <dmb-show-message v-else-if="error" retry-button @retry="init">{{ error }}</dmb-show-message>
    <dmb-show-message v-else-if="loading">Presentatie laden...</dmb-show-message>
    <dmb-show-message v-else>Geen presentatie om weer te geven</dmb-show-message>
  </div>
</template>

<script>

import {Options, Vue} from 'vue-class-component'
import {cloneDeep, compact, first, get, uniq} from 'lodash';

import StorageKey from '@/js/enum/StorageKey';
import Init from '@/gql/query/Init.gql';
import FindPresentation from '@/gql/query/FindPresentation.gql';

import {PlayerStoreAction} from '@/js/store/player';

import ShowMessage from 'common/js/component/ShowMessage.vue';
import playerMixin from 'common/js/mixin/player';
import {getAssetsFromShow} from 'common/js/util/asset';
import {generateCustomFontFaces, generateGoogleFontsUrl, getFontsFromShow} from 'common/js/util/font';
import {preloadAssets} from 'common/js/util/preload';
import RealtimeSubject from 'common/js/enum/RealtimeSubject';
import {StyleStoreMutation} from 'common/js/store/style';
import RealtimeOperation from 'common/js/enum/RealtimeOperation';
import {AssetStoreAction, AssetStoreMutation} from 'common/js/store/assets';
import ApiErrorType from 'common/js/enum/ApiErrorType';

export default Options({
  name: 'Player',
  mixins: [playerMixin],
  components: {
    [ShowMessage.name]: ShowMessage
  }
})(
    class Player extends Vue {
      loading = 0;
      error = null;
      updating = false;

      screen = null;
      show = null;

      get orientation() {
        return this.screen?.system.orientation;
      }

      get code() {
        return window.localStorage.getItem(StorageKey.SCREEN_CODE);
      }

      mounted() {
        if (!this.code) {
          this.$router.replace({name: 'login'});
        }

        this.init();
      }

      unmounted() {

        this.stopUpdating();
      }

      async init() {
        this.logger.info('Initializing...');

        this.error = null;
        this.loading++;

        try {
          await this.populatePlayerStores();

          // TODO: uncomment when font uploader is added again
          this.loadCustomFonts();
          await this.updateScreen();

          this.logger.info('Initialize completed');

          // Update Cycle
          if (!this.updating) {
            this.startUpdating();
            this.$di.realtime.connect(`SCREEN:${this.code}`);
          }
        } catch (e) {

          console.error(e);

          const apiErrors = e.networkError?.result?.errors;
          const errorType = apiErrors?.length ? apiErrors[0].type : null;

          if (errorType === ApiErrorType.ORGANIZATION_INACTIVE) {
            this.error = 'Systeem inactief. Neem contact op met DigiMenuBox.';
          } else {
            this.error = 'De presentatie kan niet worden geladen, controleer de internetverbinding';
          }
        }

        this.loading--;
      }

      async updateScreen() {
        const response = await this.$apollo.query({
          fetchPolicy: 'network-only',
          query: Init,
          variables: {
            code: this.code
          }
        });

        const screen = get(response, 'data.findSystemScreenByCode');
        if (!screen) {
          throw 'Invalid response';
        }

        this.screen = cloneDeep(screen);

        this.logger.info(`- Loaded screen ${screen.sortIndex} in system "${screen.system.title}"`);

        this.$store.commit(AssetStoreMutation.SET_LOGO_URL, screen.system.organization.logoUrl);
        await this.loadShows();

        // Determine Show
        const show = this.determineShow();
        await this.updateShow(show);
      }

      async updateShow(show) {
        if (!show) {

          this.logger.info('Clearing show');
          this.show = null;
        } else if (show.id !== this.show?.id) {
          // Preload Fonts
          this.logger.info(`- Preloading fonts...`);

          this.loadGoogleFonts(getFontsFromShow(show, show.slides));
          // TODO: uncomment when font uploader is added again
          this.loadCustomFonts();

          // Preload Assets
          this.logger.info(`- Preloading assets... 0%`);

          await preloadAssets(
              getAssetsFromShow(show, this.$store.getters.getAsset), (progress) => {
                this.logger.info(`- Preloading assets... ${progress}%`);
              }
          );

          // Switch to show
          this.logger.info(`Switching to show ${show.id}`, show);

          this.$store.commit(StyleStoreMutation.SET_SHOW_STYLE, show.style);
          this.show = show;
        }
      }

      loadGoogleFonts(fonts) {
        if (this.googleFontsLink) {

          document.head.removeChild(this.googleFontsLink);
          this.googleFontsLink = null;
        }

        if (fonts.length) {

          const googleFontsLink = document.createElement('link');
          googleFontsLink.type = 'text/css';
          googleFontsLink.rel = 'stylesheet';
          googleFontsLink.href = generateGoogleFontsUrl(fonts);

          document.head.appendChild(googleFontsLink);
          this.googleFontsLink = googleFontsLink;
        }
      }

      loadCustomFonts() {
        generateCustomFontFaces(this.$store.state.assets.assets);
      }

      async loadShows() {
        // Load shows (allocations + active campaigns)
        const allocationShowIds = this.screen.allocations.map(allocation => allocation.presentation.publishedShow?.id);
        const campaignShowIds = (this.screen.activeCampaignShows || []).map(s => s?.id);
        const showIds = uniq(compact([...allocationShowIds, ...campaignShowIds]));

        this.logger.info(`- Fetching ${showIds.length} shows (incl. ${campaignShowIds.length} campaign show(s))...`);

        for (let showId of showIds) {

          this.logger.info(`-- Fetching show ${showId}`);
          await this.$store.dispatch(PlayerStoreAction.FETCH_SHOW, showId);
        }
      }

      async reloadPresentation(presentationId) {
        this.logger.info('Replacing presentation', presentationId);

        const response = await this.$apollo.query({
          query: FindPresentation,
          variables: {
            id: presentationId
          },
          fetchPolicy: 'network-only'
        });

        const presentation = get(response, 'data.findPresentation');
        if (!presentation) {
          throw 'Invalid response';
        }

        // Replace presentation
        for (let allocation of this.screen.allocations) {

          if (allocation.presentation.id === presentation.id) {
            allocation.presentation = presentation;
          }
        }

        return presentation;
      }

      determineShow() {
        const allocations = this.screen.allocations;
        let activeAllocation = first(allocations.filter(allocation => !allocation.collection.startTime));

        if (this.screen.system.activeCollectionId) {

          const systemAllocation = allocations.find(allocation => allocation.collection.id === this.screen.system.activeCollectionId);
          activeAllocation = systemAllocation || null;
        }

        const baseShowId = activeAllocation?.presentation?.publishedShow?.id;
        const baseShow = baseShowId ? this.$store.state.player.showsById[baseShowId] : null;

        const { slides: campaignSlides, ids: campaignShowIds } = this.collectCampaignSlides();

        if (!baseShow && !campaignSlides.length) {
          return null;
        }

        if (!campaignSlides.length) {
          return baseShow;
        }

        // Append campaign slides into the rotation. Encode the active campaign-show
        // ids into the merged id so updateShow() detects a real change when a
        // campaign window opens/closes (otherwise the base id stays the same and
        // the player wouldn't refresh its slide list).
        const campaignTag = campaignShowIds.slice().sort().join(',');
        const merged = baseShow
            ? { ...baseShow, id: `${baseShow.id}:c=${campaignTag}`, slides: [...(baseShow.slides || []), ...campaignSlides] }
            : { id: `campaigns:${this.screen.id}:${campaignTag}`, slides: campaignSlides, style: null };

        return merged;
      }

      collectCampaignSlides() {
        const campaignShows = this.screen?.activeCampaignShows || [];
        const slides = [];
        const ids = [];
        for (const stub of campaignShows) {
          if (!stub?.id) continue;
          const fullShow = this.$store.state.player.showsById[stub.id];
          if (fullShow?.slides?.length) {
            slides.push(...fullShow.slides);
            ids.push(String(stub.id));
          }
        }
        return { slides, ids };
      }

      startUpdating() {
        this.startUpdatingFeeds();

        const realtime = this.$di.realtime;

        realtime.handler(RealtimeSubject.PRESENTATION, this.handlePresentationOperation.bind(this));
        realtime.handler(RealtimeSubject.SYSTEM, this.handleSystemOperation.bind(this));
        realtime.handler(RealtimeSubject.COLLECTION, this.handleCollectionOperation.bind(this));
        realtime.handler(RealtimeSubject.CAMPAIGN, this.handleCampaignOperation.bind(this));

        realtime.onReconnect(attempts => {

          setTimeout(async () => {

            if (attempts > 50) {

              document.location.reload();
            } else if (attempts > 10) {

              this.logger.info('Refetching stores & screen because of previous connection failure');

              // Refetch everything
              await this.populatePlayerStores();
              await this.updateScreen();
            }

          }, 10 * 1000);
        });
      }

      stopUpdating() {
        this.stopUpdatingFeeds();

        const realtime = this.$di.realtime;

        realtime.removeHandler(RealtimeSubject.PRESENTATION);
        realtime.removeHandler(RealtimeSubject.SYSTEM);
        realtime.removeHandler(RealtimeSubject.COLLECTION);
        realtime.removeHandler(RealtimeSubject.CAMPAIGN);
      }

      async handlePresentationOperation({data}) {
        await this.$store.dispatch(AssetStoreAction.LOAD_ASSETS);

        // Presentation changed, reload show
        if (this.show && data.presentationId.toString() === this.show.presentationId.toString()) {
          const presentation = await this.reloadPresentation(data.presentationId);
          const newShowId = presentation.publishedShow?.id;

          if (newShowId) {
            await this.$store.dispatch(PlayerStoreAction.FETCH_SHOW, newShowId);

            const show = this.determineShow();
            await this.updateShow(show);
          } else {
            await this.updateShow(null);
          }
        }
      }

      async handleSystemOperation({data, operation}) {
        if (operation === RealtimeOperation.ACTIVATE) {

          this.screen.system.activeCollectionId = data.collectionId.toString();

          const show = this.determineShow();
          await this.updateShow(show);
        } else if (operation === RealtimeOperation.UPDATE) {

          this.updateScreen();
        }
      }

      async handleCollectionOperation() {
        this.updateScreen();
      }

      async handleCampaignOperation() {
        this.updateScreen();
      }
    }
)

</script>
<style lang="scss" src="../../../scss/route/Player.scss"></style>
